<script setup lang="ts">
//#region imports
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { omit } from 'lodash-es';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import { useLodash } from '@/setup/lodash';
import { usePermissions } from '@/composables/usePermissions';
import { useTableFilter, useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useCrmStatus } from '@/views/CRM/composables/useCrmStatus';
import { saveBlob } from '@/utils/fileSaver';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
import type { GetCrmSegmentsData, GetCrmSegmentsResponse } from '@/types/openapi-ts';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { SettingsPageQuery } from '@/types/core';
import type { CrmSegmentListItem } from '@/types/crm';

//#endregion

//#region types
interface Props {
  currentBrand: string;
}

type CrmSegmentsQuery = SettingsPageQuery<GetCrmSegmentsData['query']>;

interface CombinedFilterMetaItem {
  value: string;
  label: string;
  filter: string[];
}
//#endregion

//#region define
const props = defineProps<Props>();
//#endregion

//#region const
const LS_PATH = 'CRM/Segments';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields: UiTableNewField<CrmSegmentListItem>[] = [
  {
    name: 'incrementing_id',
    sortable: 'custom',
    align: 'left',
    width: '60',
  },
  {
    name: 'moderation_status',
    label: 'computed_status',
    sortable: 'custom',
    type: 'status',
    width: '150',
    statusText: (value, row) => t(`crm.fields.computed_status.values.${detectCrmStatus(row)}`),
    status: (value, row) => detectCrmStatus(row),
  },
  {
    name: 'name',
    sortable: 'custom',
    minWidth: '100',
  },
  {
    name: 'players_count',
    label: 'segment_players_count',
    sortable: 'custom',
    minWidth: '60',
    computedValue: (row) => (row.is_players_attached
      ? `${parseFloat(String(row.players_count || 0)).toLocaleString('en-US')}`
      : t('crm.status.updating')),
  },
  {
    name: 'type',
    sortable: 'custom',
    computedValue: (row) => row.type ? t(`crm.fields.type.values.${row.type}`) : '',
    width: '120',
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const store = useTypedStore();
const updateUrl = useUpdateUrl();
const popupManager = usePopupManager();
const _ = useLodash();
const { hasPermission } = usePermissions();
const { t } = useI18n();
const { searchQuery } = useTableSearch();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'name',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});
const { filterParams, setFilter } = useTableFilter({
  lsPath: LS_PATH,
  initialOptions: {
    filter: {
      status: '',
      only_mine: false,
    },
  },
});

const {
  getStatusGroupFromList,
  sanitizeStatus,
  detectCrmStatus,
} = useCrmStatus();
//#endregion

//#region state
const tableData = ref<GetCrmSegmentsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const permissions = ref<NonNullable<GetCrmSegmentsResponse['misc']>['permissions']>({});
//#endregion

//#region computed
const isSuperUser = computed(() => store.getters['auth/adminAcl']?.is_superuser);
const view = computed(() => String(route.meta.view || 'segments'));

const viewsMeta = computed(() => ([
  {
    name: 'segments',
    staticFilter: null,
    title: t('crm.segments.title'),
    hasCreateButton: true,
  },
  {
    name: 'quality-assurance-segments',
    staticFilter: {
      status: ['active'],
      moderation_status: ['on_review'],
    },
    title: t('crm.segments.title_quality_assurance'),
    hasCreateButton: false,
  },
]));

const viewsMetaIndex = computed<Record<string, any>>(() => {
  const result: Record<string, any> = {};
  viewsMeta.value.forEach((item) => {
    result[item.name] = item;
  });
  return result;
});

const currentViewMeta = computed(() => viewsMetaIndex.value[view.value] || viewsMetaIndex.value.segments);

const combinedFilterMeta = computed<CombinedFilterMetaItem[]>(() => ([
  { value: 'draft', label: t('crm.fields.computed_status.values.draft'), filter: ['draft'] },
  { value: 'on_review', label: t('crm.fields.computed_status.values.on_review'), filter: ['active', 'on_review'] },
  { value: 'for_rework', label: t('crm.fields.computed_status.values.for_rework'), filter: ['active', 'for_rework'] },
  { value: 'approved', label: t('crm.fields.computed_status.values.approved'), filter: ['active', 'approved'] },
  { value: 'declined', label: t('crm.fields.computed_status.values.declined'), filter: ['active', 'declined'] },
]));

const combinedFilterMetaIndex = computed<Record<string, CombinedFilterMetaItem>>(() => {
  const result: Record<string, CombinedFilterMetaItem> = {};
  combinedFilterMeta.value.forEach((item) => {
    result[item.value] = item;
  });
  return result;
});

//#endregion

//#region methods
function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);
    setFilter(_urlData);
    delete _urlData.page;
    delete _urlData.limit;

    Object.assign(optionsSort.value, _.pick(_urlData, Object.keys(optionsSort.value)));
  }
  nextTick(() => {
    fetchData();
  });
}

function _query() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    search: searchQuery.value,
    offset: limit.value * page.value - limit.value,
    site_id: props.currentBrand,
  } as GetCrmSegmentsData['query'];
}

function _completedFilterUrl() {
  const _dataFilters: CrmSegmentsQuery = omit(_query(), 'offset');
  _dataFilters.page = page.value;
  _dataFilters.filters = JSON.stringify(filterParams.value);
  if(Object.keys(filterParams.value).length <= 0) {
    delete _dataFilters.filters;
  }
  updateUrl.updateGetParams(_dataFilters);
}

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  const currentStatus = combinedFilterMetaIndex.value[filterParams.value.status as string]?.filter || ['draft', 'active'];
  const dynamicFilters = currentViewMeta.value.staticFilter || getStatusGroupFromList(sanitizeStatus(currentStatus));

  options = {
    ...options,
    ...dynamicFilters,
  };
  if(filterParams.value.only_mine === true) {options.only_mine = filterParams.value.only_mine;}

  try {
    const { data: { payload, misc } } = await api.getSegments(options);

    count.value = misc!.count;
    permissions.value = misc?.permissions || {};
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function handleRefresh () {
  page.value = 1;
  fetchData();
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

function canEdit(id: string) {
  return isSuperUser.value || !!permissions.value[id]?.can_edit;
}

function isEditable(row: CrmSegmentListItem) {
  const status = detectCrmStatus(row);
  return canEdit(row.id) && (status === 'draft' || status === 'for_rework');
}

function isDelete(row: CrmSegmentListItem) {
  return !canEdit(row.id) || row.used_in_activities.length > 0;
}

async function exportData(row: CrmSegmentListItem) {
  const file = await api.getSegmentPlayers(row.id);
  const linkBlob = new Blob([file.data], { type: file.headers['content-type'] });
  saveBlob(linkBlob, row.name);
}

function handleDelete(row: CrmSegmentListItem) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('crm.confirm.delete_segment.title'),
      message: t('crm.confirm.delete_segment.msg', { segmentName: row.name }),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('crm.confirm.delete_segment.action'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteSegment(row.id);
          await fetchData();
        } finally {
          context.handleClose();
        }
      },
    },
  });
}

function viewSegmentPopup(id: string = '') {
  popupManager.viewSegmentPopup.open({
    data: {
      segmentId: id,
      currentBrand: props.currentBrand,
    },
    listeners: {
      close: fetchData,
      edit: (segmentId: string) => {
        openSegmentPopup(segmentId);
      },
    },
  });
}

function openSegmentPopup(id: string | null = null) {
  popupManager.createSegment.open({
    data: {
      segmentId: id,
      currentBrand: props.currentBrand,
    },
    listeners: {
      close: fetchData,
    },
  });
}

//
// function openCreateSegmentPopup(type: PopupType) {
//   const popupManager = usePopupManager();
//   popupManager.createSegment.open({
//     data: {
//       popupType: type,
//       segmentId: '',
//       currentBrand: props.currentBrand,
//     },
//     listeners: {
//       close: getSegments,
//     },
//   });
// }
//
// function editSegmentPopup(id: string) {
//   const popupManager = usePopupManager();
//   popupManager.createSegment.open({
//     data: {
//       popupType: 'edit',
//       segmentId: id,
//       currentBrand: props.currentBrand,
//     },
//     listeners: {
//       close: getSegments,
//     },
//   });
// }


//#endregion

//#region watch
watch(() => [props.currentBrand, route.fullPath], async () => {
  await fetchData();
});
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="segments">
    <UiToolbar>
      <template v-slot:title>
        {{ currentViewMeta.title }}
      </template>
      <template v-slot:left-slot>
        <div class="t:flex t:space-x-8 t:items-center">
          <UiInput
            v-model.trim="searchQuery"
            :width="180"
            is-search
            @search="handleRefresh"
          />
          <UiSelectElement
            v-if="!currentViewMeta.staticFilter"
            v-model="filterParams.status"
            :placeholder="$t('crm.fields.computed_status.values.all')"
            :list="combinedFilterMeta"
            option-value-param="value"
            option-name-param="label"
            option-label-param="label"
            width="170"
            clearable
            @change="fetchData()"
          />
          <UiCheckbox
            v-model="filterParams.only_mine"
            :label="$t('crm.only_mine')"
            @change="fetchData()"
          />
        </div>
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="hasPermission('crm_segments_edit') && currentViewMeta.hasCreateButton"
          filled
          icon="plus"
          @click="openSegmentPopup()"
        >
          {{ $t('crm.segments.add_segment') }}
        </UiButton>
        <UiButton
          icon="sync-alt"
          :disabled="isDataLoading"
          @click="fetchData()"
        >
          {{ $t('reports.other.refresh') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="crm"
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="130"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              :tooltip="$t('mediaBuying.info.files_card.download')"
              icon="down"
              :disabled="scope.row.moderation_status !== 'approved'"
              @click="exportData(scope.row)"
            />
            <ActionIcon
              icon="eye"
              :tooltip="$t('crm.buttons.view')"
              @click="viewSegmentPopup(scope.row.id)"
            />
            <ActionIcon
              icon="pen"
              :tooltip="$t('crm.buttons.edit')"
              :disabled="!isEditable(scope.row)"
              @click="openSegmentPopup(scope.row.id)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('crm.buttons.delete')"
              :disabled="isDelete(scope.row)"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
